import { canAdjustOrderLinePrice } from '@washday/utilities';
import {
  ActionIcon,
  Combobox,
  Grid,
  Input,
  InputBase,
  Loader,
  Select,
  Stack,
  TextInput,
  useCombobox,
  Group,
  Switch,
  Text,
  Divider,
  Flex,
  Button,
  Tooltip,
  Menu,
  Badge
} from '@mantine/core';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useGetStores } from '../../../hooks/stores/queries';
import { useGetCustomers } from '../../../hooks/customers/queries';
import { formatMoneyString, getCustomerFullName } from '../../../utils/strings';
import _ from 'lodash';
import {
  IconEdit,
  IconPlus,
  IconSearch,
  IconClock,
  IconReceipt,
  IconTrash,
  IconDiscount,
  IconCoins,
  IconCircleCheck,
  IconMinus,
  IconDotsVertical
} from '@tabler/icons-react';
import { ICustomer } from '../../../interfaces/Customer';
import { ICashierBox, IStore } from '../../../interfaces/Store';
import { OrderContext } from '../../../context/OrderContext';
import { OrderDTO, OrderProduct } from '../interfaces';
import { ISection } from '../../../interfaces/Section';
import PaymentConfigEnum, { MercadoPagoEnum } from '../../../enum/PaymentConfig';
import { useDisclosure } from '@mantine/hooks';
import AddDiscountCodeModal from './AddDiscountCodeModal';
import AddCustomerModal from './AddCustomerModal';
import EditCustomerModal from './EditCustomerModal';
import { useFormContext } from 'react-hook-form';
import AddAttributesModal from './AddAttributesModal';
import PickupDeliveryTime from './PickupDeliveryTime';
import OrderTotalSummary from './OrderTotalSummary';
import moment from 'moment';
import { showNotification } from '../../../utils/Notification/Notification';
import { useCreateOrder, useUpdateOrder } from '../../../hooks/orders/mutations';
import { MonitoringEventTypes } from '../../../utils/monitoring/Monitoring_Events_Enum';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from '../../../utils/monitoring/posthog';
import { tryPrintTicket } from '../../../utils/ticketHelper';
import { useSelector } from 'react-redux';
import { OrderFormValues } from '../order.schema';
import OrderStatuses from '../../../enum/OrderStatuses';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import styles from '../NewOrder.module.css';
import { useDebouncedValue } from '@mantine/hooks';
import { PaymentMethodsEnum } from '../../../enum/PaymentMethodsEnum';
import { ChangeCalculatorModal } from '../../../components/ChangeCalculatorModal';
import { RedeemPointsModal } from './RedeemPointsModal';
import { MercadoPagoPaymentSheet } from '../../../components/MercadoPagoPaymentSheet';
import OrderCreatedDrawer from '../../../components/OrderCreatedDrawer';
import OrderPaymentLinesModal from '../../../components/OrderModals/OrderPaymentLinesModal';
import { useOrderPaymentLinesModal } from '../../../hooks/useOrderPaymentLinesModal';
import useCanUseMercadoPago from '../../../hooks/useCanUseMercadoPago';
import { buildOrderFolio } from '../../../utils/ordersUtils';
import ProductType from '../../../enum/ProductType';
import { getMeasuredUnitLabel } from '../../../utils/helpers/getAllPieces';
import {
  normalizeAttributesForLineType,
  isMeasuredLineType,
  normalizeProductIdentity,
  normalizeSelectedOptionIdentity,
  resolveLineId
} from '../utils/lineIdentity';
import { recalculateOrderProductsWithLineIds } from '../utils/recalculateOrderProducts';
import {
  buildCreateOrderPayload,
  buildStoreReceptionValidationUpdatePayload,
  buildUpdateOrderPayload
} from '../utils/orderPayloadBuilders';
import { mergeCreatedOrderUpdate } from '../utils/orderCreatedUpdate';
import { GarmentAttributeCatalog } from '../../../interfaces/Company';
import {
  autoOpenCashDrawerForPayments,
  getOrderCreatePaymentsForAutoOpen
} from '../../../utils/cashDrawer';
import {
  getCustomerPhoneValue,
  isWhatsappNotificationMissingCustomerPhone,
  WHATSAPP_MISSING_CUSTOMER_PHONE_MESSAGE,
} from '../utils/whatsappNotifications';
import { hasPermission } from '../../../utils/permission-utils';
import { getOrderLineCharge } from '../utils/minimumBillableQuantity';
import { useCustomerDisplay } from '../../../customerDisplay/CustomerDisplayProvider';
import { buildWebDisplayContent } from '../../../customerDisplay/orderContent';
import type { CustomerDisplayPaymentOverride } from '@washday/utilities';
import { useGetOrderById } from '../../../hooks/orders/queries';
import { useRequestCashierPinValidation } from '../../../hooks/useRequestCashierPinValidation';
import { getStoreByIdV2 } from '../../../services/store.service';
import type { ValidatedPinUser } from 'washday-sdk';

const NEW_ORDER_CUSTOMER_SEARCH_SCOPE = 'new-order-customer-search';

type OrderSummarySection = {
  _id: string;
  name: string;
  products: OrderProduct[];
};

const resolveSectionId = (section: unknown): string => {
  if (typeof section === 'string') return section;
  if (!section || typeof section !== 'object') return '';
  return String((section as { _id?: unknown })._id ?? '');
};

const resolvePopulatedSectionName = (section: unknown): string => {
  if (!section || typeof section !== 'object') return '';
  return String((section as { name?: unknown }).name ?? '');
};

const OrderSummary = ({
  sections,
  storeDiscounts,
  resetOrder,
  store,
  cashierboxes,
  garmentAttributeCatalogs,
  isStoreReceptionValidationMode = false,
  onStoreReceptionValidationSubmitStart,
  onStoreReceptionValidationSuccess,
  onStoreReceptionValidationSubmitEnd,
}: {
  sections: ISection[];
  storeDiscounts: any;
  resetOrder: () => void;
  store: IStore | null;
  cashierboxes: ICashierBox[];
  garmentAttributeCatalogs: GarmentAttributeCatalog[];
  isStoreReceptionValidationMode?: boolean;
  onStoreReceptionValidationSubmitStart?: () => void;
  onStoreReceptionValidationSuccess?: () => void;
  onStoreReceptionValidationSubmitEnd?: () => void;
}) => {
  const { t } = useTranslation(['NewOrder']);
  const { company, user } = useSelector((state) => state.user);
  const { session: displaySession } = useCustomerDisplay();
  const { data: displayStores } = useGetStores();
  const displayStore = useMemo(() => store ? ({ ...store, country: displayStores?.find((candidate) => candidate._id === store._id)?.country || store.country }) : store, [store, displayStores]);
  const [displayOrderId, setDisplayOrderId] = useState('');
  const displayOrderIdRef = useRef('');
  const displayMounted = useRef(true);
  const manualDisplayCollection = useRef<{ orderId: string; opened: boolean; pending: boolean } | null>(null);
  const trackDisplayOrder = (id: string) => {
    if (manualDisplayCollection.current?.orderId !== id) manualDisplayCollection.current = null;
    displayOrderIdRef.current = id; setDisplayOrderId(id);
  };
  const displayPaymentState = useRef<CustomerDisplayPaymentOverride | undefined>(undefined);
  const { data: displayConfirmedOrder, refetch: refetchDisplayOrder } = useGetOrderById(displayOrderId);
  const lastDisplayedOrder = useRef<unknown>(null);
  const displayResultTimer = useRef<ReturnType<typeof setTimeout>>();
  const armDisplayResultTimeout = () => {
    clearTimeout(displayResultTimer.current);
    if (manualDisplayCollection.current) return;
    displayResultTimer.current = setTimeout(() => trackDisplayOrder(''), 8_000);
  };
  useEffect(() => {
    displayMounted.current = true;
    return () => { displayMounted.current = false; clearTimeout(displayResultTimer.current); };
  }, []);
  useEffect(() => {
    if (!displayConfirmedOrder || displayConfirmedOrder === lastDisplayedOrder.current || !displaySession) return;
    if (manualDisplayCollection.current?.orderId === displayConfirmedOrder._id) return;
    lastDisplayedOrder.current = displayConfirmedOrder;
    if (displayPaymentState.current === 'processing' || displayPaymentState.current === 'pending') return;
    if (getValues('products')?.length || getValues('buyAndGetProducts')?.length) {
      trackDisplayOrder('');
      return;
    }
    displaySession.complete(buildWebDisplayContent(displayConfirmedOrder, displayStore, company, displayPaymentState.current));
    armDisplayResultTimeout();
  }, [displayConfirmedOrder, displaySession, displayStore, company]);
  const userCanViewCustomers = hasPermission(user, 'customers', 'canView');
  const userCanCreateCustomers = hasPermission(user, 'customers', 'canCreate');
  const userCanEditCustomers = hasPermission(user, 'customers', 'canEdit');
  const { watch, setValue, getValues, handleSubmit, register, clearErrors } = useFormContext();
  const total = watch('total');
  const location = useLocation();
  const [createdOrder, setCreatedOrder] = useState<any>(null);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const { storeCanUseMP } = useCanUseMercadoPago(searchParamStoreId);
  const {
    setUpdatingQuantityProduct,
    setUpdatingQuantityProductModal,
    setUpdatingProductPrice,
    setEditProductPriceModal,
    isUpdatingOrder,
    currentUpdatingOrder,
    setLastAddedOrderProduct
  } = useContext(OrderContext);
  const { requestPin } = useRequestCashierPinValidation();
  const updatingOrderStoreId = (typeof currentUpdatingOrder?.createdIn === 'string'
    ? currentUpdatingOrder.createdIn : currentUpdatingOrder?.createdIn?._id) || currentUpdatingOrder?.store;
  const updatePinRequest = useRef<AbortController | null>(null);
  useEffect(() => () => { updatePinRequest.current?.abort(); }, [currentUpdatingOrder?._id, updatingOrderStoreId]);
  const whatsappMissingPhoneOptions = {
    blockNewOrdersIfWhatsappCustomerPhoneMissing:
      store?.orderPageConfig?.blockNewOrdersIfWhatsappCustomerPhoneMissing ?? true,
    isUpdatingOrder,
  };
  const [
    openedAddDiscountCodeModal,
    { open: openAddDiscountCodeModal, close: closeAddDiscountCodeModal }
  ] = useDisclosure(false);
  const [
    openedUseLoyaltyPointsModal,
    { open: openUseLoyaltyPointsModal, close: closeUseLoyaltyPointsModal }
  ] = useDisclosure(false);
  const [
    openedChangeCalculatorModal,
    { open: openChangeCalculatorModal, close: closeChangeCalculatorModal }
  ] = useDisclosure(false);
  const [openedAddCustomerModal, { open: openAddCustomerModal, close: closeAddCustomerModal }] =
    useDisclosure(false);
  const [openedEditCustomerModal, { open: openEditCustomerModal, close: closeEditCustomerModal }] =
    useDisclosure(false);
  const [
    openedAddAttributesModal,
    { open: openAddAttributesModal, close: closeAddAttributesModal }
  ] = useDisclosure(false);
  const [openedMercadoPagoModal, { open: openMercadoPagoModal, close: closeMercadoPagoModal }] =
    useDisclosure(false);
  const [
    openedOrderCreatedDrawer,
    { open: openOrderCreatedDrawer, close: closeOrderCreatedDrawer }
  ] = useDisclosure(false);
  const submitInFlightRef = useRef(false);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const {
    opened: openedPaymentLinesModal,
    order: paymentLinesOrder,
    pinUser: paymentLinesPinUser,
    title: paymentLinesTitle,
    open: openPaymentLinesModal,
    close: closePaymentLinesModal,
    syncOrder: syncPaymentLinesOrder
  } = useOrderPaymentLinesModal({ store });

  // Abre el sheet SOLO si hay attemptId (ya creada la orden/attempt)
  const shouldShowMpSheet = openedMercadoPagoModal && Boolean(createdOrder?.mpAttempt?.attemptId);

  const [showPickupDelivery, setShowPickupDelivery] = useState(false);
  const [showOrderTotalSummary, setShowOrderTotalSummary] = useState(false);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(customerSearchInput, 300);
  const {
    cancel: cancelCustomerSearch,
    data: customersData,
    isLoading: isGetCustomersLoading
  } = useGetCustomers(
    {
      searchTerm: debouncedSearchTerm,
      limit: 10,
      createdIn: searchParamStoreId
    },
    userCanViewCustomers &&
      ((!!debouncedSearchTerm && debouncedSearchTerm.length > 1) || !debouncedSearchTerm),
    { scope: NEW_ORDER_CUSTOMER_SEARCH_SCOPE }
  );
  const { data: stores } = useGetStores(true);
  const history = useHistory();
  const [sectionsMap, setSectionsMap] = useState<Record<string, OrderSummarySection>>({});
  const createOrder = useCreateOrder();
  const updateOrder = useUpdateOrder();
  const posthog = usePostHog();
  const isSubmitDisabled = submitInFlight || createOrder.isPending || updateOrder.isPending;

  const paymentMethods = useMemo(() => {
    const baseOptions = (
      Object.keys(PaymentConfigEnum) as Array<keyof typeof PaymentConfigEnum>
    ).map((paymentConfigKey) => ({
      value: PaymentConfigEnum[paymentConfigKey],
      label: t(`FieldName:paymentMethods.${PaymentConfigEnum[paymentConfigKey]}`)
    }));
    if (storeCanUseMP && !isUpdatingOrder) {
      //maybe no need to validate isUpdatingOrder since payment method is not visible in the update order form
      return [
        ...baseOptions,
        {
          value: MercadoPagoEnum.MERCADOPAGO,
          label: t(`FieldName:paymentMethods.${MercadoPagoEnum.MERCADOPAGO}`)
        }
      ];
    }
    return baseOptions;
  }, [storeCanUseMP, isUpdatingOrder]);

  // Add express watcher
  const express = watch('express');
  const orderType = watch('orderType');
  const returnItemsChargeMode = watch('returnItemsChargeMode');
  const returnPricing = { orderType, returnItemsChargeMode };
  const paymentMethod = watch('paymentMethod');
  const deliveryInfo = watch('deliveryInfo');
  const orderStatusWatch = watch('status');
  const buyAndGetProductsWatch = watch('buyAndGetProducts');
  const discountCodeWatch = watch('discountCodeObj');
  const showStatusSelector = isUpdatingOrder && !isStoreReceptionValidationMode;
  const isReturnOrder = isUpdatingOrder && orderType === 'return';
  // Use functions to get latest values when needed
  const getLatestValues = () => ({
    discountCodeObj: getValues('discountCodeObj'),
    express: getValues('express'),
    customerInfo: getValues('customerInfo'),
    orderType: getValues('orderType'),
    returnItemsChargeMode: getValues('returnItemsChargeMode')
  });

  const getLineProductId = (line: any): string => normalizeProductIdentity(line);
  const getLineOptionId = (line: any): string | null => normalizeSelectedOptionIdentity(line);
  const formatMeasuredQuantity = (value: unknown): string => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '';
    }

    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(numericValue);
  };
  const getSummaryMeasuredLabels = (line: any) => {
    const measuredType = isMeasuredLineType(line);
    if (!measuredType) {
      const quantityLabel = `${line?.quantity || ''}`;
      return {
        isMeasured: false,
        quantityLabel,
        nameLabel: line?.name || '',
        pricingQuantityLabel: quantityLabel,
        pricingUnitLabel: '',
        minimumChargeLabel: ''
      };
    }

    const measuredQuantity = formatMeasuredQuantity(line?.quantity);
    const measuredUnit = getMeasuredUnitLabel(line?.type, store?.measurementUnits);
    const measuredQuantityLabel = [measuredQuantity, measuredUnit].filter(Boolean).join(' ');
    const measuredSuffix = measuredQuantityLabel ? ` (${measuredQuantityLabel})` : '';
    const charge = getOrderLineCharge(line, Boolean(express), returnPricing);
    const billableQuantity = formatMeasuredQuantity(charge.billableQuantity);
    const billableQuantityLabel = [billableQuantity, measuredUnit].filter(Boolean).join(' ');

    return {
      isMeasured: true,
      quantityLabel: '1',
      nameLabel: `${line?.name || ''}${measuredSuffix}`,
      pricingQuantityLabel: measuredQuantityLabel || measuredQuantity || `${line?.quantity || ''}`,
      pricingUnitLabel: measuredUnit || '',
      minimumChargeLabel: charge.minimumApplied
        ? t('NewOrder:minimumCharge', {
            quantity: billableQuantity,
            unit: measuredUnit || ''
          })
        : ''
    };
  };

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    }
  });

  const customers = useMemo(() => {
    return customersData?.pages?.reduce((list, { customers }: { customers: ICustomer[] }) => {
      list.push(...customers);
      return list;
    }, [] as ICustomer[]);
  }, [customersData]);

  const getCustomerSearchPrimaryLabel = (customer: ICustomer) =>
    `${customer.customerNumber || '-'} - ${getCustomerFullName(customer.name, customer.lastName)}`;

  const getCustomerSearchSecondaryLabel = (customer: ICustomer) =>
    [getCustomerPhoneValue(customer), customer.email]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' / ');

  const options =
    customers?.map((item: ICustomer) => {
      const secondaryLabel = getCustomerSearchSecondaryLabel(item);

      return (
        <Combobox.Option
          data-testid={`new-order-customer-option-${item._id || ''}`}
          value={item._id || ''}
          key={item._id}>
          <Stack gap={2}>
            <Text size="sm" lineClamp={1}>
              {getCustomerSearchPrimaryLabel(item)}
            </Text>
            {secondaryLabel ? (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {secondaryLabel}
              </Text>
            ) : null}
          </Stack>
        </Combobox.Option>
      );
    }) || [];

  // Handle store changes
  const handleStoreChange = (newStoreId: string | null) => {
    if (!newStoreId) return;
    void cancelCustomerSearch();
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('storeId', newStoreId || '');
    history.push({ search: newSearchParams.toString() });
  };

  useEffect(() => () => {
    void cancelCustomerSearch();
  }, [cancelCustomerSearch]);

  const openWhatsappMissingCustomerPhoneModal = () => {
    ConfirmationModal({
      title: t('NewOrder:whatsappCustomerPhoneRequiredTitle'),
      children: <Text>{t('NewOrder:whatsappCustomerPhoneRequiredMessage')}</Text>,
      labels: {
        confirm: t('NewOrder:whatsappCustomerPhoneRequiredConfirm'),
        cancel: ''
      },
      cancelProps: {
        style: {
          display: 'none'
        }
      },
      onConfirm: () => {},
      onCancel: () => {}
    });
  };

  const syncSelectedCustomer = (customer?: ICustomer) => {
    const previousCustomerId = getValues('customer');
    const nextCustomerId = customer?._id || '';
    if (isReturnOrder && nextCustomerId !== previousCustomerId) return;
    const isDifferentCustomer =
      isUpdatingOrder && Boolean(nextCustomerId) && nextCustomerId !== previousCustomerId;

    if (isDifferentCustomer) {
      setValue('address', customer?.address ?? null);
    }
    setValue('customer', nextCustomerId);
    setValue('phone', getCustomerPhoneValue(customer));
    setValue('loyaltyPointsRedeemed', 0);
    setValue('loyaltyDiscountAmount', 0);
    setValue('customerInfo', customer ? { ...customer } : undefined);

    if (
      isWhatsappNotificationMissingCustomerPhone({
        ...getValues(),
        customerInfo: customer,
      }, whatsappMissingPhoneOptions)
    ) {
      openWhatsappMissingCustomerPhoneModal();
      return;
    }

    clearErrors('notifyBy');
  };

  const handleCustomerChange = (val: string) => {
    if (!val || isReturnOrder) return;
    const customer = customers?.find((customer: ICustomer) => customer._id === val);
    syncSelectedCustomer(customer);
    combobox.closeDropdown();
  };

  const buildOrderSummarySectionsMap = (products: OrderProduct[]) => {
    const newSectionsMap: Record<string, OrderSummarySection> = {};

    sections.forEach((section) => {
      const sectionId = section._id || '';
      if (!sectionId) return;
      newSectionsMap[sectionId] = {
        _id: sectionId,
        name: section.name || '',
        products: []
      };
    });

    products.forEach((product) => {
      const sectionId = resolveSectionId(product.section);
      if (!sectionId || newSectionsMap[sectionId]) return;

      const historicalSectionName = resolvePopulatedSectionName(product.section);
      if (!historicalSectionName) return;

      newSectionsMap[sectionId] = {
        _id: sectionId,
        name: historicalSectionName,
        products: []
      };
    });

    return newSectionsMap;
  };

  const generateSectionsMap = () => {
    setSectionsMap(buildOrderSummarySectionsMap(getValues('products') || []));
  };

  const groupProductsBySection = () => {
    try {
      const products = getValues('products');
      // TODO: MAYBE WE NEED TO USE THE ORDER.PRODUCTS AND ORDER.BUYANDGETPRODUCTS
      const buyAndGetProducts = getValues('buyAndGetProducts');
      const newSectionsMap = buildOrderSummarySectionsMap(products);

      products.forEach((prod: OrderProduct) => {
        const sectionId = resolveSectionId(prod.section);
        if (sectionId && newSectionsMap[sectionId]) {
          newSectionsMap[sectionId].products.push(prod);
        }
      });
      setSectionsMap(newSectionsMap);
    } catch (e) {
      console.log(e, getValues('products'), getValues('buyAndGetProducts'));
    }
  };

  const removeDiscountCode = () => {
    const { newOrderProds, buyAndGetProds } = recalculateOrderProductsWithLineIds({
      discountCodeObj: undefined,
      products: getValues('products'),
      express: getValues('express'),
      storeDiscounts,
      customerInfo: getValues('customerInfo'),
      previousBuyAndGetProducts: getValues('buyAndGetProducts'),
      orderType: getValues('orderType'),
      returnItemsChargeMode: getValues('returnItemsChargeMode')
    });
    setValue('discountCode', '');
    setValue('discountCodeObj', undefined);
    setValue('products', newOrderProds);
    setValue('buyAndGetProducts', buyAndGetProds);
  };

  const handleOrderSummaryClick = () => {
    setShowOrderTotalSummary(!showOrderTotalSummary);
    setShowPickupDelivery(false);
  };

  const handlePickupDeliveryClick = () => {
    setShowPickupDelivery(!showPickupDelivery);
    setShowOrderTotalSummary(false);
  };

  const printTicket = async (order) => {
    try {
      // CUSTOMER TICKET
      if (store?.orderPageConfig?.printTicketAfterOrder) {
        await tryPrintTicket(order, store, t, 'ticketStructure', company.logoUrl, true, company?.companyTaxInfo);
      }
      if (store?.printingConfig?.printTicketForLaundry) {
        // LAUNDRY TICKET
        await tryPrintTicket(order, store, t, 'ticketForLaundryStructure', company.logoUrl, true, company?.companyTaxInfo);
      }
      return;
    } catch (e) {
      console.log(e);
    }
  };

  const handleCloseOrderCreatedDrawer = () => {
    closeOrderCreatedDrawer();
    setCreatedOrder(null);
  };

  const handleStartNewOrder = () => {
    resetOrder();
    setShowPickupDelivery(false);
    setShowOrderTotalSummary(false);
  };

  const handleViewOrder = (orderId: string) => {
    history.push(`/orders/${orderId}?storeId=${searchParamStoreId}`);
  };

  const handleCollectPayment = (orderId: string) => {
    if (!createdOrder || createdOrder?._id !== orderId) return;
    const orderToCollect = createdOrder;
    openPaymentLinesModal(createdOrder).then((opened) => {
      if (!displayMounted.current) return;
      // IF THE PAYMENT LINES MODAL IS OPEN, CLOSE THE ORDER CREATED DRAWER
      if (opened) {
        manualDisplayCollection.current = { orderId, opened: true, pending: false };
        clearTimeout(displayResultTimer.current);
        displayPaymentState.current = 'pending';
        trackDisplayOrder(orderId);
        const content = buildWebDisplayContent(orderToCollect, displayStore, company, 'pending');
        displaySession?.payment(content.payment, content);
        handleCloseOrderCreatedDrawer();
      }
    });
  };

  const refreshManualDisplayOrder = async (orderId: string) => {
    const collection = manualDisplayCollection.current;
    if (!displayMounted.current || !orderId || collection?.orderId !== orderId) return;
    const { data, isError } = await refetchDisplayOrder();
    if (!displayMounted.current || manualDisplayCollection.current !== collection || isError || !data || data._id !== orderId || displayOrderIdRef.current !== orderId) return;
    if (getValues('products')?.length || getValues('buyAndGetProducts')?.length) return;
    syncPaymentLinesOrder(data);
    lastDisplayedOrder.current = data;
    displayPaymentState.current = undefined;
    displaySession?.complete(buildWebDisplayContent(data, displayStore, company));
    if (collection && !collection.opened && !collection.pending) {
      manualDisplayCollection.current = null;
      armDisplayResultTimeout();
    }
  };

  const handleCloseManualCollection = () => {
    const collection = manualDisplayCollection.current;
    if (collection) collection.opened = false;
    closePaymentLinesModal();
    if (collection && !collection.pending) void refreshManualDisplayOrder(collection.orderId);
  };

  const handleManualPaymentPendingChange = (pending: boolean) => {
    const collection = manualDisplayCollection.current;
    if (!displayMounted.current || !collection) return;
    collection.pending = pending;
    if (pending) {
      displayPaymentState.current = 'pending';
      if (paymentLinesOrder?._id === collection.orderId) {
        const content = buildWebDisplayContent(paymentLinesOrder, displayStore, company, 'pending');
        displaySession?.payment(content.payment, content);
      }
    } else if (!collection.opened) void refreshManualDisplayOrder(collection.orderId);
  };

  const handleOrderCreatedDrawerOrderUpdated = useCallback((updatedOrder: any) => {
    setCreatedOrder((currentOrder: any) => mergeCreatedOrderUpdate(currentOrder, updatedOrder));
  }, []);

  const onCreateOrder = async (data: OrderFormValues) => {
    let orderRegistered = false;
    try {
      const submitOrderData = buildCreateOrderPayload(data);
      const externalPayment = submitOrderData.paymentMethod === MercadoPagoEnum.MERCADOPAGO && storeCanUseMP;
      if (externalPayment) {
        displayPaymentState.current = 'processing';
        displaySession?.payment({ status: 'processing', amountDue: data.total });
      }
      const res = await createOrder.mutateAsync(submitOrderData as any);
      if (res) {
        orderRegistered = true;
        trackDisplayOrder(res._id);
        if (!externalPayment) {
          displayPaymentState.current = undefined;
          displaySession?.complete(buildWebDisplayContent(res, displayStore, company));
          armDisplayResultTimeout();
        } else if (!res?.mpAttempt?.attemptId) {
          displayPaymentState.current = res?.mpAttemptError ? 'failed' : undefined;
          displaySession?.complete(buildWebDisplayContent(res, displayStore, company, displayPaymentState.current));
          armDisplayResultTimeout();
        } else {
          displayPaymentState.current = 'pending';
          const content = buildWebDisplayContent(res, displayStore, company, 'pending');
          displaySession?.payment(content.payment, content);
        }
        //IF ORDER SUCCESS  SEND TRACK EVENT AND NOTIFICATION
        trackEvent(posthog, MonitoringEventTypes.ORDER_PAGE_ORDER_CREATE_SUCCESS, {
          _id: res._id,
          sequence: buildOrderFolio(res?.folioPrefix, res?.sequence)
        });
        showNotification({
          type: 'Default',
          title: t('NewOrder:orderCreated')
        });

        if (submitOrderData.paymentMethod === MercadoPagoEnum.MERCADOPAGO && storeCanUseMP) {
          const mpAttemptError: { code: number; message: string } | undefined = res?.mpAttemptError;
          if (res?.mpAttempt?.attemptId) {
            trackEvent(posthog, MonitoringEventTypes.MERCADO_PAGO_ATTEMPT_CREATED_SUCCESS, {
              orderId: res._id,
              amount: res.total,
              currency: res.currency,
              storeId: store?._id
            });
            setCreatedOrder({ ...res, cashierBoxId: submitOrderData.cashierBox });
            openMercadoPagoModal(); // ahora sí, ya hay attemptId
          } else {
            showNotification({
              autoClose: false,
              type: 'Error',
              title:
                'No se pudo iniciar el cobro en Mercado Pago, revise que no tenga otro pedido en curso en terminal (El registro de pedido si se ha creado).',
              message: mpAttemptError?.message
            });
          }
        } else {
          // Show order-created UI first so printer issues (hang/fail) never block the flow
          setCreatedOrder(res);
          openOrderCreatedDrawer();
          void autoOpenCashDrawerForPayments({
            cashDrawerConfig: store?.cashDrawerConfig,
            payments: getOrderCreatePaymentsForAutoOpen({
              amountPaid: submitOrderData.amountPaid,
              paymentMethod: submitOrderData.paymentMethod,
              prepaidAmount: submitOrderData.prepaidAmount,
              prepaidPaymentMethod: submitOrderData.prepaidPaymentMethod,
            }),
            source: 'payment',
          }).then((cashDrawerResult) => {
            if (cashDrawerResult.attempted && !cashDrawerResult.success) {
              showNotification({
                type: 'Error',
                title: t('Notifications:cashDrawerOpenFailedTitle'),
                message: cashDrawerResult.errorMessage || t('Notifications:cashDrawerOpenFailedBody')
              });
            }
          });
          // Print ticket in background; do not await so missing/hanging printer does not block UI
          void printTicket(res);
        }

        //reset order form
        resetOrder();
        // REGENERATE SECTIONS MAP AND GROUP PRODUCTS BY SECTION
        generateSectionsMap();
        groupProductsBySection();
        setShowPickupDelivery(false);
        setShowOrderTotalSummary(false);
      } else {
        displayPaymentState.current = undefined;
        displaySession?.orderError();
        setCreatedOrder(null);
        trackEvent(posthog, MonitoringEventTypes.ORDER_PAGE_ORDER_CREATE_FAIL, {
          error: 'No se pudo crear el pedido',
          data: submitOrderData
        });
      }
    } catch (e) {
      if (!orderRegistered) {
        displayPaymentState.current = undefined;
        displaySession?.orderError();
      }
      trackEvent(posthog, MonitoringEventTypes.ORDER_PAGE_ORDER_CREATE_FAIL, {
        error: e
      });
      console.log('onCreateOrder error', e);
    }
  };

  const onUpdateOrder = async (data: OrderFormValues) => {
    const isValidationSubmit = isStoreReceptionValidationMode;
    let collectionRequest: AbortController | undefined;

    if (isValidationSubmit) {
      onStoreReceptionValidationSubmitStart?.();
    }

    try {
      const updateOrderObj = isStoreReceptionValidationMode
        ? buildStoreReceptionValidationUpdatePayload(currentUpdatingOrder, data)
        : buildUpdateOrderPayload(currentUpdatingOrder, data);
      let pinUser: ValidatedPinUser | null = null;
      if (updateOrderObj.status === OrderStatuses.COLLECTED && currentUpdatingOrder?.status !== OrderStatuses.COLLECTED) {
        collectionRequest = new AbortController();
        updatePinRequest.current = collectionRequest;
        const sourceStore = updatingOrderStoreId === store?._id
          ? store : await getStoreByIdV2(updatingOrderStoreId) as unknown as IStore;
        if (collectionRequest.signal.aborted) return;
        if (!updatingOrderStoreId || !sourceStore || sourceStore._id !== updatingOrderStoreId) throw new Error(t('Notifications:somethingWrong'));
        if (sourceStore.requireCashierPinToCollect) {
          pinUser = await requestPin({ purpose: 'order-collection', storeId: updatingOrderStoreId, signal: collectionRequest.signal });
          if (!pinUser?._id || collectionRequest.signal.aborted) return;
        }
      }
      if (pinUser) Object.assign(updateOrderObj, {
        pinUserId: pinUser._id,
        ...(pinUser.pinValidationToken ? { pinValidationToken: pinUser.pinValidationToken } : {})
      });
      const res = await updateOrder.mutateAsync({
        id: currentUpdatingOrder?._id,
        data: updateOrderObj
      });
      if (isValidationSubmit) {
        onStoreReceptionValidationSuccess?.();
      }
      showNotification({
        type: 'Default',
        title: isStoreReceptionValidationMode
          ? t('NewOrder:storeReceptionValidation.success')
          : t('NewOrder:orderUpdated')
      });
      //Print Ticket if store settings is configured and restart order form
      resetOrder();
      // REGENERATE SECTIONS MAP AND GROUP PRODUCTS BY SECTION
      history.goBack();
      return;
    } catch (e) {
      showNotification({
        type: 'Error',
        title: isStoreReceptionValidationMode
          ? 'No se pudo validar en sucursal el pedido'
          : 'No se pudo actualizar el pedido'
      });
    } finally {
      if (updatePinRequest.current === collectionRequest) updatePinRequest.current = null;
      if (isValidationSubmit) {
        onStoreReceptionValidationSubmitEnd?.();
      }
    }
  };

  const onSubmit = async (data: any) => {
    if (submitInFlightRef.current || createOrder.isPending || updateOrder.isPending) {
      return;
    }

    submitInFlightRef.current = true;
    setSubmitInFlight(true);

    try {
      if (!isUpdatingOrder && !data?.cashierBox) {
        showNotification({
          type: 'Error',
          title: 'Caja registradora es requerido',
          message: 'Selecciona una caja registradora antes de guardar el pedido.'
        });
        return;
      }

      if (isUpdatingOrder) {
        await onUpdateOrder(data);
      } else {
        if (data.paymentMethod === PaymentMethodsEnum.Cash) {
          openChangeCalculatorModal();
        } else {
          //NO ES NECESARIO UNA CALCULADORA
          await onCreateOrder(data);
        }
      }
    } catch (e) {
      console.log('onSubmit error', e);
    } finally {
      submitInFlightRef.current = false;
      setSubmitInFlight(false);
    }
  };

  const getErrorMessages = (fieldErrors: any) => {
    const errorMessages: string[] = [];
    Object.keys(fieldErrors).forEach((key) => {
      if (fieldErrors[key].message) {
        errorMessages.push(fieldErrors[key].message);
      } else if (typeof fieldErrors[key] === 'object' && fieldErrors[key] !== null) {
        errorMessages.push(...getErrorMessages(fieldErrors[key]));
      }
    });
    return errorMessages;
  };

  const onError = (fieldErrors: any) => {
    if (
      fieldErrors?.notifyBy?.message === WHATSAPP_MISSING_CUSTOMER_PHONE_MESSAGE &&
      isWhatsappNotificationMissingCustomerPhone(getValues(), whatsappMissingPhoneOptions)
    ) {
      openWhatsappMissingCustomerPhoneModal();
      return;
    }

    const errorMessages: string[] = getErrorMessages(fieldErrors);

    showNotification({
      type: 'Error',
      title: 'Error',
      message: errorMessages.join('\n')
    });
  };

  const onRemoveLoyaltyPoints = () => {
    ConfirmationModal({
      title: t('NewOrder:removeLoyaltyPointsConfirmation'),
      onConfirm: () => {
        setValue('loyaltyPointsRedeemed', 0);
        setValue('loyaltyDiscountAmount', 0);
      },
      onCancel: () => {}
    });
  };

  const updateOrderProducts = useCallback(
    (newProducts: OrderProduct[]) => {
      // Get latest values at the time of update
      const { discountCodeObj, express, customerInfo, orderType, returnItemsChargeMode } =
        getLatestValues();
      const { newOrderProds, buyAndGetProds } = recalculateOrderProductsWithLineIds({
        discountCodeObj,
        products: newProducts,
        express,
        storeDiscounts,
        customerInfo,
        previousBuyAndGetProducts: getValues('buyAndGetProducts'),
        orderType,
        returnItemsChargeMode
      });
      setValue('products', newOrderProds);
      setValue('buyAndGetProducts', buyAndGetProds);
    },
    [storeDiscounts, setValue, getValues]
  );

  const handleIncrementClick = (product: OrderProduct, quantity: number = 1) => {
    if (product.returnSourceOrderLineId) return;
    const products = getValues('products') || [];
    const targetLineId = product.lineId || product.orderProductLineID;
    let prodIdx = -1;
    if (targetLineId) {
      prodIdx = (products || []).findIndex((line: any) => resolveLineId(line) === targetLineId);
    } else {
      const targetProductId = getLineProductId(product);
      const targetOptionId = getLineOptionId(product);
      prodIdx = (products || []).findIndex((line: any) => {
        return (
          getLineProductId(line) === targetProductId && getLineOptionId(line) === targetOptionId
        );
      });
    }

    if (prodIdx === -1) {
      return;
    }

    const productsCopy = [...(products || [])];
    const currentLine = productsCopy[prodIdx];
    const previousQuantity = currentLine.quantity || 0;
    const nextQuantity = previousQuantity + quantity;

    if (nextQuantity <= 0) {
      productsCopy.splice(prodIdx, 1);
    } else {
      let nextAttributes;
      if (isMeasuredLineType(currentLine)) {
        nextAttributes = normalizeAttributesForLineType(currentLine, 1);
      } else if (nextQuantity > previousQuantity) {
        // add new attribute object when incrementing quantity
        nextAttributes = currentLine.attributes
          ? [...currentLine.attributes, {}]
          : [{}];
      } else {
        nextAttributes = normalizeAttributesForLineType(currentLine, nextQuantity);
      }

      productsCopy[prodIdx] = {
        ...currentLine,
        quantity: nextQuantity,
        attributes: nextAttributes
      };
      // allow user to set attributes for the product
      setLastAddedOrderProduct(productsCopy[prodIdx]);
    }
    updateOrderProducts(productsCopy);
  };

  const showUpdateQuantityModal = (product: OrderProduct) => {
    if (product.returnSourceOrderLineId) return;
    setUpdatingQuantityProduct(product);
    setUpdatingQuantityProductModal(true);
  };

  const showAddExtraAmountModal = (product: OrderProduct) => {
    if (!canAdjustOrderLinePrice(product)) return;
    setUpdatingProductPrice(product);
    setEditProductPriceModal(true);
  };

  useEffect(() => {
    // Initial grouping when sections change
    generateSectionsMap();
    groupProductsBySection();

    const subProductsChanges = watch((value, { name }) => {
      if (!name || name === 'products' || name === 'buyAndGetProducts') {
        groupProductsBySection();
      }
    });

    return () => {
      subProductsChanges.unsubscribe();
    };
  }, [sections]);

  return (
    <div
      className={styles.stickyRightCol}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%' // Make the container take full height
      }}>
      <MercadoPagoPaymentSheet
        onPaymentStateChange={(status, confirmedOrder) => {
          if (status === 'processing' || status === 'pending') {
            if (createdOrder?._id) trackDisplayOrder(createdOrder._id);
            clearTimeout(displayResultTimer.current);
            displayPaymentState.current = status;
            displaySession?.payment({ status, amountDue: createdOrder?.total });
          } else {
            displayPaymentState.current = status === 'confirmed' ? undefined : status;
            const resultOrderId = createdOrder?._id;
            if (status !== 'confirmed') {
              if (confirmedOrder && createdOrder) displaySession?.complete(buildWebDisplayContent({ ...createdOrder, ...confirmedOrder }, displayStore, company, displayPaymentState.current));
              armDisplayResultTimeout();
            }
            void refetchDisplayOrder().then(({ data }) => {
              if (!displayMounted.current || !data || data._id !== resultOrderId || displayOrderIdRef.current !== resultOrderId) return;
              if (getValues('products')?.length || getValues('buyAndGetProducts')?.length) return;
              displaySession?.complete(buildWebDisplayContent(data, displayStore, company, displayPaymentState.current));
              armDisplayResultTimeout();
            });
          }
        }}
        opened={shouldShowMpSheet}
        onClose={() => {
          closeMercadoPagoModal();
        }}
        attemptId={createdOrder?.mpAttempt?.attemptId ?? undefined}
        order={createdOrder ?? undefined}
        autoCloseOnApproved={false}
        onApproved={(approvedOrder: any) => {
          void autoOpenCashDrawerForPayments({
            cashDrawerConfig: store?.cashDrawerConfig,
            payments: [
              {
                paymentMethod: MercadoPagoEnum.MERCADOPAGO,
                amountPaid:
                  Number(approvedOrder?.prepaidAmount || createdOrder?.prepaidAmount || 0) ||
                  Number(approvedOrder?.mpAttempt?.amount || createdOrder?.mpAttempt?.amount || 0) ||
                  Math.max(
                    0,
                    Number(approvedOrder?.total || createdOrder?.total || 0) -
                      Number(approvedOrder?.amountPaid || createdOrder?.amountPaid || 0)
                  )
              }
            ],
            source: 'payment',
          }).then((cashDrawerResult) => {
            if (cashDrawerResult.attempted && !cashDrawerResult.success) {
              showNotification({
                type: 'Error',
                title: t('Notifications:cashDrawerOpenFailedTitle'),
                message: cashDrawerResult.errorMessage || t('Notifications:cashDrawerOpenFailedBody')
              });
            }
          });
          void printTicket(approvedOrder || createdOrder);
        }}
        onPrintTicket={(order) => printTicket(order)}
      />
      <OrderCreatedDrawer
        opened={openedOrderCreatedDrawer}
        onClose={handleCloseOrderCreatedDrawer}
        order={createdOrder}
        store={store}
        onStartNewOrder={handleStartNewOrder}
        onViewOrder={handleViewOrder}
        onCollectPayment={handleCollectPayment}
        onOrderUpdated={handleOrderCreatedDrawerOrderUpdated}
      />
      <OrderPaymentLinesModal
        onAddPaymentLine={() => refreshManualDisplayOrder(paymentLinesOrder?._id || '')}
        onPaymentPendingChange={handleManualPaymentPendingChange}
        title={paymentLinesTitle}
        close={handleCloseManualCollection}
        opened={openedPaymentLinesModal}
        order={paymentLinesOrder}
        onDeletePaymentLine={() => refreshManualDisplayOrder(paymentLinesOrder?._id || '')}
        onUpdatePaymentLine={() => refreshManualDisplayOrder(paymentLinesOrder?._id || '')}
        pinUser={paymentLinesPinUser}
      />
      <ChangeCalculatorModal
        opened={openedChangeCalculatorModal}
        onClose={closeChangeCalculatorModal}
        onConfirm={async () => {
          const data: any = getValues();
          await onCreateOrder(data);
          closeChangeCalculatorModal();
        }}
        orderTotal={total}
        currencySymbol={store?.currencySymbol}
      />
      <AddAttributesModal
        opened={openedAddAttributesModal}
        close={closeAddAttributesModal}
        title={t('NewOrder:addAttributes')}
        garmentAttributeCatalogs={garmentAttributeCatalogs}
      />
      <AddCustomerModal
        opened={openedAddCustomerModal}
        onClose={closeAddCustomerModal}
        afterSubmit={(customer: ICustomer) => {
          syncSelectedCustomer(customer);
          closeAddCustomerModal();
        }}
      />
      <EditCustomerModal
        opened={openedEditCustomerModal}
        onClose={closeEditCustomerModal}
        afterSubmit={(customer: ICustomer) => {
          syncSelectedCustomer(customer);
          closeEditCustomerModal();
        }}
      />
      <RedeemPointsModal
        opened={openedUseLoyaltyPointsModal}
        onClose={closeUseLoyaltyPointsModal}
        customerId={getValues('customer')}
        storeId={searchParamStoreId || ''}
        orderTotal={total}
        onConfirm={(data) => {
          setValue('loyaltyPointsRedeemed', data.pointsUsed);
          setValue('loyaltyDiscountAmount', data.discount);
          closeUseLoyaltyPointsModal();
        }}
      />
      <AddDiscountCodeModal
        opened={openedAddDiscountCodeModal}
        onClose={closeAddDiscountCodeModal}
        storeDiscounts={storeDiscounts}
      />
      <Stack
        p={'md'}
        gap={'xs'}
        className={styles.contentWrapper}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          margin: 0
        }}>
        {!showOrderTotalSummary && !showPickupDelivery && (
          <Stack className={styles.mainContent}>
            {showStatusSelector && (
              <Select
                clearable={false}
                allowDeselect={false}
                placeholder={t('FieldName:status')}
                value={orderStatusWatch}
                data={
                  Object.keys(OrderStatuses).map((statusKey) => {
                    return {
                      label: t(
                        `FieldName:orderStatuses.${
                          OrderStatuses[statusKey as keyof typeof OrderStatuses]
                        }`
                      ),
                      value: OrderStatuses[statusKey as keyof typeof OrderStatuses]
                    };
                  }) || []
                }
                onChange={(val) => {
                  const currentStatus = getValues('status');
                  ConfirmationModal({
                    children: (
                      <Text>
                        Cambiar el estado de un pedido puede afectar el seguimiento de su inventario
                        e insumos, ya que estos no se actualizarán automáticamente como de
                        costumbre. Si está seguro de actualizar el pedido, por favor confirme.
                      </Text>
                    ),
                    onCancel: () => {
                      // TODO: REVERT STATUS
                      setValue('status', currentStatus);
                    },
                    onConfirm: () => {
                      setValue('status', val);
                    }
                  });
                }}
              />
            )}
            {!isUpdatingOrder && (
              <Select
                clearable={false}
                allowDeselect={false}
                value={searchParamStoreId || null}
                onChange={handleStoreChange}
                placeholder={t('FieldName:store')}
                data={
                  stores?.map((store) => {
                    return {
                      label: store.name,
                      value: store._id
                    };
                  }) || []
                }
              />
            )}
            <Grid justify="space-between" align="center">
              <Grid.Col span={9} pr={0}>
                <Combobox
                  store={combobox}
                  withinPortal={false}
                  onOptionSubmit={handleCustomerChange}>
                  <Combobox.Target>
                    <InputBase
                      data-testid="new-order-customer-select"
                      component="button"
                      type="button"
                      pointer
                      disabled={!userCanViewCustomers || isReturnOrder}
                      rightSection={<Combobox.Chevron />}
                      onClick={() => {
                        if (userCanViewCustomers && !isReturnOrder) {
                          combobox.toggleDropdown();
                        }
                      }}
                      rightSectionPointerEvents="none">
                      {getValues('customerInfo') ? (
                        <Text lineClamp={1}>
                          {getCustomerFullName(
                            getValues('customerInfo').name,
                            getValues('customerInfo').lastName
                          )}
                        </Text>
                      ) : (
                        <Input.Placeholder>{t('FieldName:customer')}</Input.Placeholder>
                      )}
                    </InputBase>
                  </Combobox.Target>
                  <Combobox.Dropdown>
                    <Combobox.Search
                      data-testid="new-order-customer-search"
                      onChange={(event) => {
                        void cancelCustomerSearch();
                        setCustomerSearchInput(event.currentTarget.value);
                      }}
                      placeholder={t('FieldName:searchCustomer')}
                      rightSection={
                        isGetCustomersLoading ? <Loader size={16} /> : <IconSearch size={16} />
                      }
                    />
                    <Combobox.Options>
                      {options?.length > 0 ? (
                        options
                      ) : (
                        <Combobox.Empty>Sin resultados</Combobox.Empty>
                      )}
                    </Combobox.Options>
                  </Combobox.Dropdown>
                </Combobox>
              </Grid.Col>
              <Grid.Col span={3}>
                <Group grow>
                  <Tooltip label="Crear cliente">
                    <ActionIcon
                      variant="outline"
                      color={userCanCreateCustomers && !isReturnOrder ? '#14a1e6' : 'gray'}
                      title="Agregar cliente"
                      disabled={!userCanCreateCustomers || isReturnOrder}
                      onClick={openAddCustomerModal}>
                      <IconPlus size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Editar cliente">
                    <ActionIcon
                      disabled={!getValues('customerInfo') || !userCanEditCustomers}
                      variant="outline"
                      color={
                        getValues('customerInfo') && userCanEditCustomers ? '#14a1e6' : 'gray'
                      }
                      onClick={openEditCustomerModal}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Grid.Col>
            </Grid>
            <Flex justify="space-between" align="center">
              <Text>{t('FieldName:express')}</Text>
              <Switch
                checked={express} // use watched express value
                onChange={(event) => {
                  setValue('express', event.currentTarget.checked);
                }}
              />
            </Flex>
            <Grid
              style={{
                flex: '1 1 auto',
                minHeight: 100, //for super small screen this dissapears if we don't set a min height
                overflowY: 'auto',
                overflowX: 'hidden',
                margin: 0
              }}>
              <Grid.Col span={12}>
                {discountCodeWatch && (
                  <Text fw={'bold'}>Codigo de descuento: {discountCodeWatch?.name}</Text>
                )}
                <div>
                  {buyAndGetProductsWatch?.map((product: any) => {
                    const lineCharge = getOrderLineCharge(
                      product,
                      Boolean(express),
                      returnPricing
                    );
                    const isReturnOriginLine = Boolean(product.returnSourceOrderLineId);
                    const nominalReturnLineTotal =
                      'nominalTotal' in lineCharge
                        ? Number(lineCharge.nominalTotal)
                        : 0;
                    const lineKey =
                      product.lineId || product.orderProductLineID || resolveLineId(product);
                    const { isMeasured, quantityLabel, nameLabel, pricingQuantityLabel, pricingUnitLabel, minimumChargeLabel } =
                      getSummaryMeasuredLabels(product);
                    const pricingUnitSuffix = pricingUnitLabel ? `/${pricingUnitLabel}` : '';
                    const pricingUnitAmount = lineCharge.unitPrice;
                    return (
                      <Grid key={lineKey}>
                        <Grid.Col span="content">
                          <Text size="sm">{quantityLabel}</Text>
                        </Grid.Col>
                        <Grid.Col span="auto">
                          <Stack gap={0}>
                            <Text
                              lineClamp={1}
                              onClick={isReturnOriginLine ? undefined : openAddAttributesModal}
                              style={{ cursor: isReturnOriginLine ? 'default' : 'pointer' }}
                              size="sm">
                              {nameLabel}
                            </Text>
                            {isReturnOriginLine ? (
                              <Badge size="xs" variant="light" color="blue">
                                {t('NewOrder:operationalReturns.returnOriginLine')}
                              </Badge>
                            ) : null}
                            <Text size="xs" c={'dimmed'}>
                              {pricingQuantityLabel} x{' '}
                              {`${formatMoneyString(pricingUnitAmount)}${pricingUnitSuffix}`}
                            </Text>
                            {isReturnOriginLine && nominalReturnLineTotal > 0 && lineCharge.total === 0 ? (
                              <Text
                                size="xs"
                                c="dimmed"
                                style={{ textDecoration: 'line-through' }}>
                                {formatMoneyString(nominalReturnLineTotal)}
                              </Text>
                            ) : null}
                            {minimumChargeLabel && (
                              <Text size="xs" c={'dimmed'}>{minimumChargeLabel}</Text>
                            )}
                          </Stack>
                        </Grid.Col>
                        <Grid.Col span="content">
                          <Stack gap={0}>
                            <Text size={'sm'} td={'none'}>
                              {formatMoneyString(lineCharge.total.toFixed(2))}
                            </Text>
                          </Stack>
                        </Grid.Col>
                      </Grid>
                    );
                  })}
                </div>

                {Object.values(sectionsMap || {}).map((section) => {
                  if (section.products.length > 0) {
                    return (
                      <div key={`${section._id}-${section.name}-OrderSummary`}>
                        <Text lineClamp={1} fw={'bold'}>
                          {section.name}
                        </Text>
                        {section.products?.map((product: OrderProduct) => {
                          const productDiscountAmount = product.discountAmount || 0;
                          const lineCharge = getOrderLineCharge(
                            product,
                            Boolean(express),
                            returnPricing
                          );
                          const isReturnOriginLine = Boolean(product.returnSourceOrderLineId);
                          const nominalReturnLineTotal =
                            'nominalTotal' in lineCharge
                              ? Number(lineCharge.nominalTotal)
                              : 0;
                          const productUnitPriceWithoutDiscount = lineCharge.unitPriceBeforeDiscount;
                          const productAttributes = product.attributes || [];
                          let productAttributesStrs: string[] = [];
                          if (productAttributes.length > 0) {
                            productAttributes.forEach((attr) => {
                              productAttributesStrs.push(
                                Object.values({
                                  ...attr,
                                  _id: undefined
                                })
                                  .filter((value) => value)
                                  .join(', ')
                              );
                            });
                            productAttributesStrs = productAttributesStrs.filter((attr) => attr);
                          }
                          const lineKey =
                            product.lineId || product.orderProductLineID || resolveLineId(product);
                          const { quantityLabel, nameLabel, pricingQuantityLabel, pricingUnitLabel, minimumChargeLabel } =
                            getSummaryMeasuredLabels(product);
                          const pricingUnitSuffix = pricingUnitLabel ? `/${pricingUnitLabel}` : '';
                          return (
                            <div key={lineKey}>
                              <Grid>
                                <Grid.Col span={12} pb={0}>
                                  <Grid align="center">
                                    <Grid.Col span="content">
                                      <Text size="sm">{quantityLabel}</Text>
                                    </Grid.Col>
                                    <Grid.Col span="auto">
                                      <Stack gap={2}>
                                        <Text
                                          lineClamp={1}
                                          onClick={
                                            isReturnOriginLine ? undefined : openAddAttributesModal
                                          }
                                          style={{
                                            cursor: isReturnOriginLine ? 'default' : 'pointer'
                                          }}
                                          size="sm">
                                          {nameLabel}
                                        </Text>
                                        {isReturnOriginLine ? (
                                          <Badge size="xs" variant="light" color="blue">
                                            {t('NewOrder:operationalReturns.returnOriginLine')}
                                          </Badge>
                                        ) : null}
                                      </Stack>
                                    </Grid.Col>
                                    <Grid.Col span="content">
                                      {!isReturnOriginLine ? <Flex gap={4}>
                                        <ActionIcon
                                          size="md"
                                          color="red"
                                          onClick={() => handleIncrementClick(product, -1)}>
                                          <IconMinus size={16} />
                                        </ActionIcon>

                                        <ActionIcon
                                          size="md"
                                          color="green"
                                          onClick={() => handleIncrementClick(product, 1)}>
                                          <IconPlus size={16} />
                                        </ActionIcon>

                                        <Menu shadow="md" width={200}>
                                          <Menu.Target>
                                            <ActionIcon size="md" color="blue" variant="light">
                                              <IconDotsVertical size={16} />
                                            </ActionIcon>
                                          </Menu.Target>

                                          <Menu.Dropdown>
                                            <Menu.Item
                                              onClick={() => {
                                                showUpdateQuantityModal(product as any);
                                              }}>
                                              <Text>{t('NewOrder:updateQuantity')}</Text>
                                            </Menu.Item>

                                            <Menu.Item
                                              disabled={!canAdjustOrderLinePrice(product)}
                                              onClick={() => {
                                                showAddExtraAmountModal(product as any);
                                              }}>
                                              <Text>{t('NewOrder:addExtraAmount')}</Text>
                                            </Menu.Item>
                                          </Menu.Dropdown>
                                        </Menu>
                                      </Flex> : null}
                                    </Grid.Col>
                                  </Grid>
                                </Grid.Col>
                                <Grid.Col span={12} pb={0}>
                                  <Flex display="flex" justify="space-between" align="center">
                                    <Flex gap={2}>
                                      <Stack gap={0}>
                                        <Text size="xs" c={'dimmed'} mb={'auto'}>
                                          {`${pricingQuantityLabel} x ${formatMoneyString(
                                            lineCharge.unitPrice
                                          )}${pricingUnitSuffix}`}
                                        </Text>
                                        {isReturnOriginLine && nominalReturnLineTotal > 0 && lineCharge.total === 0 ? (
                                          <Text
                                            size="xs"
                                            c="dimmed"
                                            style={{ textDecoration: 'line-through' }}>
                                            {formatMoneyString(nominalReturnLineTotal)}
                                          </Text>
                                        ) : null}
                                        {minimumChargeLabel && (
                                          <Text size="xs" c={'dimmed'}>{minimumChargeLabel}</Text>
                                        )}
                                      </Stack>
                                      {productDiscountAmount ? (
                                        <Text
                                          size="xs"
                                          c={'dimmed'}
                                          style={{ textDecoration: 'line-through' }}>
                                          ({formatMoneyString(
                                            productUnitPriceWithoutDiscount
                                          )}${pricingUnitSuffix})
                                        </Text>
                                      ) : (
                                        ''
                                      )}
                                    </Flex>
                                    <Flex gap={2}>
                                      <Text size={'sm'}>
                                        {formatMoneyString(lineCharge.total.toFixed(2))}
                                      </Text>
                                    </Flex>
                                  </Flex>
                                </Grid.Col>
                                {productAttributesStrs && productAttributesStrs.length > 0 && (
                                  <Grid.Col span={12}>
                                    {productAttributesStrs.map((attr) => {
                                      return (
                                        <Text size="xs" c={'dimmed'}>
                                          {attr}
                                        </Text>
                                      );
                                    })}
                                  </Grid.Col>
                                )}
                              </Grid>
                              {<Divider my="xs" />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                })}
              </Grid.Col>
            </Grid>
            <Flex align="center" gap={'xs'}>
              <Button
                style={{
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}
                rightSection={<IconDiscount size={16} />}
                variant="filled"
                color="#14a1e6"
                flex={'1 1 auto'}
                onClick={openAddDiscountCodeModal}>
                {getValues('discountCodeObj')
                  ? getValues('discountCodeObj').name
                  : t('NewOrder:addDiscountCode')}
              </Button>
              {getValues('discountCodeObj') && (
                <ActionIcon variant="outline" color="red" onClick={removeDiscountCode}>
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Flex>
            <TextInput placeholder={t('FieldName:notes')} {...register('notes')} />
            {!isUpdatingOrder && (
              <Select
                value={paymentMethod}
                disabled={getValues('prepaidPaymentMethod')}
                allowDeselect={false}
                placeholder={t('FieldName:paymentMethod')}
                data={paymentMethods.map((paymentMethod: { value: string; label: string }) => ({
                  value: paymentMethod.value,
                  label: paymentMethod.label
                }))}
                onChange={(value: string | null) => {
                  setValue('paymentMethod', value);
                }}
              />
            )}
            {store?.loyaltySettings?.enabled && (
              <Flex>
                <Button
                  rightSection={
                    getValues('loyaltyPointsRedeemed') > 0 ? (
                      <IconCircleCheck size={16} />
                    ) : (
                      <IconCoins size={16} />
                    )
                  }
                  variant="outline"
                  color={getValues('loyaltyPointsRedeemed') > 0 ? 'green' : '#14a1e6'}
                  styles={{
                    root: {
                      borderColor: getValues('loyaltyPointsRedeemed') > 0 ? '#2db370' : '#14a1e6'
                    }
                  }}
                  flex="1 1 auto"
                  disabled={
                    isUpdatingOrder || getValues('customerInfo')?.loyaltyRedemptionDisabled || false
                  }
                  onClick={
                    getValues('loyaltyPointsRedeemed') > 0
                      ? onRemoveLoyaltyPoints
                      : openUseLoyaltyPointsModal
                  }>
                  {getValues('loyaltyPointsRedeemed') > 0
                    ? `${getValues('loyaltyPointsRedeemed')} ${t('NewOrder:loyaltyPointsApplied')}`
                    : t('NewOrder:useLoyaltyPoints')}
                </Button>
              </Flex>
            )}
          </Stack>
        )}
        {showPickupDelivery && (
          <div className={styles.mainContent}>
            <PickupDeliveryTime setShowPickupDelivery={setShowPickupDelivery} />
          </div>
        )}
        {showOrderTotalSummary && (
          <div className={styles.mainContent}>
            <OrderTotalSummary
              cashierboxes={cashierboxes || []}
              setShowOrderTotalSummary={setShowOrderTotalSummary}
              store={store}
              resetOrder={() => {
                resetOrder();
                setShowOrderTotalSummary(false);
                setShowPickupDelivery(false);
                generateSectionsMap();
                groupProductsBySection();
              }}
            />
          </div>
        )}
        <Divider />
        <Flex justify="space-between" align="center" gap={'xs'}>
          <Button
            data-testid="new-order-submit"
            type="submit"
            loading={isSubmitDisabled}
            disabled={isSubmitDisabled}
            onClick={handleSubmit(onSubmit, onError)}
            fullWidth
            variant="filled"
            flex={'1 1 auto'}
            style={{
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease'
            }}
            leftSection={
              <Text
                fw={'bold'}
                style={{
                  textTransform: 'uppercase',
                  textAlign: 'left'
                }}>
                {isStoreReceptionValidationMode
                  ? t('NewOrder:storeReceptionValidation.confirmSubmit')
                  : moment(deliveryInfo.date).format('dddd DD MMM')}
              </Text>
            }
            rightSection={
              <Text fw={'bold'} ta="right">
                {formatMoneyString(total)}
              </Text>
            }
          />
          <Tooltip label={t('NewOrder:pickupDeliveryTooltip')}>
            <ActionIcon
              variant={showPickupDelivery ? 'filled' : 'outline'}
              color="#14a1e6"
              onClick={handlePickupDeliveryClick}>
              <IconClock size={16} /> {/* this is order pickup and deliveyr time icon */}
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('NewOrder:orderSummaryTooltip')}>
            <ActionIcon
              variant={showOrderTotalSummary ? 'filled' : 'outline'}
              color="#14a1e6"
              onClick={handleOrderSummaryClick}>
              <IconReceipt size={16} /> {/*This is order summary button */}
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Stack>
    </div>
  );
};

export default memo(OrderSummary);
